import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";

/* ─── Constants ─── */
const REFERRAL_SOURCES = [
    { value: "self", label: "Self / Walk-in" },
    { value: "health_center", label: "Health Centre" },
    { value: "another_hospital", label: "Another Hospital" },
    { value: "private_clinic", label: "Private Clinic" },
];

const ONSET_TYPES = [
    { value: "spontaneous", label: "Spontaneous" },
    { value: "induced", label: "Induced" },
    { value: "augmented", label: "Augmented" },
    { value: "no_labour", label: "No Labour (Elective CS)" },
];

const MEMBRANE_STATUS = [
    { value: "intact", label: "Intact" },
    { value: "ruptured", label: "Ruptured" },
];

const FETAL_PRESENTATIONS = [
    { value: "cephalic", label: "Cephalic (Head)" },
    { value: "breech", label: "Breech" },
    { value: "transverse", label: "Transverse" },
    { value: "oblique", label: "Oblique" },
];

const FETAL_POSITIONS = [
    { value: "LOA", label: "LOA — Left Occiput Anterior" },
    { value: "ROA", label: "ROA — Right Occiput Anterior" },
    { value: "LOP", label: "LOP — Left Occiput Posterior" },
    { value: "ROP", label: "ROP — Right Occiput Posterior" },
    { value: "LOT", label: "LOT — Left Occiput Transverse" },
    { value: "ROT", label: "ROT — Right Occiput Transverse" },
    { value: "LSA", label: "LSA — Left Sacrum Anterior" },
    { value: "RSA", label: "RSA — Right Sacrum Anterior" },
];

const FETAL_LIES = [
    { value: "longitudinal", label: "Longitudinal" },
    { value: "transverse", label: "Transverse" },
    { value: "oblique", label: "Oblique" },
];

const ADMISSION_STATUSES = [
    { value: "admitted", label: "Admitted" },
    { value: "in_labour", label: "In Labour" },
    { value: "delivered", label: "Delivered" },
    { value: "transferred", label: "Transferred" },
    { value: "discharged", label: "Discharged" },
];

const RISK_FACTORS = [
    { key: "previous_cs", label: "Previous Caesarean Section", severity: "high" },
    { key: "multiple_pregnancy", label: "Multiple Pregnancy (Twins/Triplets)", severity: "high" },
    { key: "preeclampsia", label: "Pre-eclampsia / Eclampsia", severity: "critical" },
    { key: "antepartum_haemorrhage", label: "Antepartum Haemorrhage", severity: "critical" },
    { key: "malpresentation", label: "Malpresentation (Breech/Transverse)", severity: "high" },
    { key: "premature_labour", label: "Premature Labour (<37 weeks)", severity: "high" },
    { key: "prolonged_rupture", label: "Prolonged Rupture of Membranes (>18hrs)", severity: "high" },
    { key: "grand_multipara", label: "Grand Multipara (≥5 deliveries)", severity: "medium" },
    { key: "anemia", label: "Anaemia (Hb <10g/dL)", severity: "medium" },
    { key: "hiv_positive", label: "HIV Positive", severity: "medium" },
    { key: "diabetes", label: "Diabetes / Gestational Diabetes", severity: "medium" },
    { key: "hypertension", label: "Chronic Hypertension", severity: "medium" },
    { key: "post_dates", label: "Post-dates (>42 weeks)", severity: "high" },
    { key: "iugr", label: "Intrauterine Growth Restriction", severity: "high" },
    { key: "oligohydramnios", label: "Oligohydramnios", severity: "medium" },
    { key: "polyhydramnios", label: "Polyhydramnios", severity: "medium" },
    { key: "bad_obstetric_history", label: "Bad Obstetric History", severity: "high" },
    { key: "age_risk", label: "Age Risk (<18 or >35 years)", severity: "medium" },
];

const now = function() {
    const d = new Date();
    const pad = function(n) { return n < 10 ? "0" + n : "" + n; };
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) +
        "T" + pad(d.getHours()) + ":" + pad(d.getMinutes());
};

const INITIAL_FORM = {
    admission_datetime: now(),
    referral_source: "",
    referral_reason: "",
    gestational_age_weeks: "",
    onset_type: "",
    membrane_status: "",
    membrane_rupture_time: "",
    initial_cervical_dilation_cm: "",
    initial_fetal_hr: "",
    initial_bp_systolic: "",
    initial_bp_diastolic: "",
    initial_pulse: "",
    initial_temperature: "",
    fetal_presentation: "",
    fetal_position: "",
    fetal_lie: "",
    pelvic_assessment: "",
    plan: "",
    status: "admitted",
};

/**
 * useLabourAdmission — Hook for labour ward admission management
 */
export const useLabourAdmission = function(actions, patient_number, visit_id) {

    const _bookingData = useState(null);
    const bookingData = _bookingData[0];
    const setBookingData = _bookingData[1];

    const _loadingBooking = useState(true);
    const loadingBooking = _loadingBooking[0];
    const setLoadingBooking = _loadingBooking[1];

    const _admissionRecord = useState(null);
    const admissionRecord = _admissionRecord[0];
    const setAdmissionRecord = _admissionRecord[1];

    const _loadingAdmission = useState(false);
    const loadingAdmission = _loadingAdmission[0];
    const setLoadingAdmission = _loadingAdmission[1];

    const _formData = useState(Object.assign({}, INITIAL_FORM));
    const formData = _formData[0];
    const setFormData = _formData[1];

    const _riskFactors = useState([]);
    const riskFactors = _riskFactors[0];
    const setRiskFactors = _riskFactors[1];

    const _saving = useState(false);
    const saving = _saving[0];
    const setSaving = _saving[1];

    const _activeSection = useState("admission");
    const activeSection = _activeSection[0];
    const setActiveSection = _activeSection[1];

    const _admissions = useState([]);
    const admissions = _admissions[0];
    const setAdmissions = _admissions[1];

    const _loadingBoard = useState(false);
    const loadingBoard = _loadingBoard[0];
    const setLoadingBoard = _loadingBoard[1];

    const _activeTab = useState("form");
    const activeTab = _activeTab[0];
    const setActiveTab = _activeTab[1];

    /* ═══ Fetch ANC booking ═══ */
    useEffect(function() {
        if (!patient_number) return;
        setLoadingBooking(true);
        axios
            .get(config.smsUrl + "/obgyn/anc/booking/" + patient_number)
            .then(function(res) {
                const data = res.data || {};
                if (data.id) {
                    setBookingData(data);
                    /* Auto-populate GA from booking LNMP */
                    if (data.lnmp) {
                        const lnmpDate = new Date(data.lnmp);
                        const todayDate = new Date();
                        const diffMs = todayDate - lnmpDate;
                        if (diffMs > 0) {
                            const weeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));
                            setFormData(function(prev) {
                                return Object.assign({}, prev, { gestational_age_weeks: String(weeks) });
                            });
                        }
                    }
                }
            })
            .catch(function() { setBookingData(null); })
            .finally(function() { setLoadingBooking(false); });
    }, [patient_number]);

    /* ═══ Check existing admission ═══ */
    useEffect(function() {
        if (!patient_number || !visit_id) return;
        setLoadingAdmission(true);
        axios
            .get(config.smsUrl + "/obgyn/labour/admission", {
                params: { patient_number: patient_number, visit_id: visit_id }
            })
            .then(function(res) {
                const data = res.data || {};
                if (data.id) {
                    setAdmissionRecord(data);
                    const populated = Object.assign({}, INITIAL_FORM);
                    Object.keys(INITIAL_FORM).forEach(function(key) {
                        if (data[key] !== undefined && data[key] !== null) {
                            populated[key] = data[key];
                        }
                    });
                    setFormData(populated);
                    if (data.risk_factors && Array.isArray(data.risk_factors)) {
                        setRiskFactors(data.risk_factors);
                    }
                }
            })
            .catch(function() {})
            .finally(function() { setLoadingAdmission(false); });
    }, [patient_number, visit_id]);

    /* ═══ Fetch labour ward board ═══ */
    const fetchBoard = useCallback(function() {
        setLoadingBoard(true);
        axios
            .get(config.smsUrl + "/obgyn/labour/admissions")
            .then(function(res) {
                const data = res.data || {};
                const list = data.admissions || data.data || [];
                if (Array.isArray(list)) setAdmissions(list);
            })
            .catch(function() {})
            .finally(function() { setLoadingBoard(false); });
    }, []);

    useEffect(function() {
        fetchBoard();
    }, [fetchBoard]);

    /* ═══ Form handlers ═══ */
    const handleChange = useCallback(function(field, value) {
        setFormData(function(prev) {
            const obj = {};
            obj[field] = value;
            return Object.assign({}, prev, obj);
        });
    }, []);

    const toggleRiskFactor = useCallback(function(key) {
        setRiskFactors(function(prev) {
            const idx = prev.indexOf(key);
            if (idx >= 0) {
                const next = prev.slice();
                next.splice(idx, 1);
                return next;
            }
            return prev.concat([key]);
        });
    }, []);

    /* ═══ Save admission ═══ */
    const saveAdmission = useCallback(function() {
        if (!patient_number) return;
        setSaving(true);

        const payload = Object.assign({}, formData, {
            patient_number: patient_number,
            visit_id: visit_id,
            booking_id: bookingData ? bookingData.id : null,
            risk_factors: riskFactors,
        });

        const isUpdate = admissionRecord && admissionRecord.id;
        const request = isUpdate
            ? axios.put(config.smsUrl + "/obgyn/labour/admission/" + admissionRecord.id, payload)
            : axios.post(config.smsUrl + "/obgyn/labour/admit", payload);

        request
            .then(function(res) {
                const data = res.data;
                if (!data) {
                    throw new Error("Invalid labour admission response");
                }
                if (!isUpdate) {
                    if (!data.id) {
                        throw new Error("Invalid labour admission response");
                    }
                    setAdmissionRecord(data);
                } else {
                    setAdmissionRecord(Object.assign({}, admissionRecord || {}, payload));
                }
                fetchBoard();
                if (actions && actions.snackbarActions) {
                    actions.snackbarActions.snackSuccess("Labour ward admission saved successfully");
                }
            })
            .catch(function() {
                if (actions && actions.snackbarActions) {
                    actions.snackbarActions.snackError("Failed to save labour ward admission");
                }
            })
            .finally(function() { setSaving(false); });
    }, [patient_number, visit_id, bookingData, formData, riskFactors, admissionRecord, actions, fetchBoard]);

    /* ═══ Update admission status ═══ */
    const updateStatus = useCallback(function(admissionId, newStatus) {
        axios
            .put(config.smsUrl + "/obgyn/labour/admission/" + admissionId, { status: newStatus })
            .then(function() {
                fetchBoard();
                if (actions && actions.snackbarActions) {
                    actions.snackbarActions.snackSuccess("Status updated to " + newStatus);
                }
            })
            .catch(function() {
                if (actions && actions.snackbarActions) {
                    actions.snackbarActions.snackError("Failed to update status");
                }
            });
    }, [actions, fetchBoard]);

    /* ═══ Computed: Gestational age from booking ═══ */
    const gestationalAge = useMemo(function() {
        if (!bookingData || !bookingData.lnmp) return null;
        const lnmpDate = new Date(bookingData.lnmp);
        const todayDate = new Date();
        const diffMs = todayDate - lnmpDate;
        if (diffMs < 0) return null;
        const weeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));
        const days = Math.floor((diffMs % (7 * 24 * 60 * 60 * 1000)) / (24 * 60 * 60 * 1000));
        return { weeks: weeks, days: days, display: weeks + "+" + days };
    }, [bookingData]);

    /* ═══ Computed: Alerts ═══ */
    const alerts = useMemo(function() {
        const list = [];
        const bp_s = Number(formData.initial_bp_systolic);
        const bp_d = Number(formData.initial_bp_diastolic);
        const fhr = Number(formData.initial_fetal_hr);
        const temp = Number(formData.initial_temperature);
        const pulse = Number(formData.initial_pulse);
        const ga = Number(formData.gestational_age_weeks);

        if (bp_s >= 140 || bp_d >= 90) {
            list.push({ type: "critical", msg: "Hypertension: BP " + bp_s + "/" + bp_d + " — Evaluate for pre-eclampsia", icon: "🔴" });
        }
        if (fhr && (fhr < 110 || fhr > 160)) {
            list.push({ type: "critical", msg: "Fetal HR " + fhr + " bpm — Outside normal range (110-160)", icon: "💓" });
        }
        if (temp && temp >= 38) {
            list.push({ type: "warning", msg: "Fever: Temperature " + temp + "°C — Evaluate for infection", icon: "🌡" });
        }
        if (pulse && pulse > 100) {
            list.push({ type: "warning", msg: "Tachycardia: Pulse " + pulse + " bpm", icon: "💗" });
        }
        if (ga && ga < 37) {
            list.push({ type: "warning", msg: "Preterm: Gestational age " + ga + " weeks (<37)", icon: "⚠" });
        }
        if (ga && ga > 42) {
            list.push({ type: "critical", msg: "Post-dates: Gestational age " + ga + " weeks (>42)", icon: "⚠" });
        }
        if (formData.membrane_status === "ruptured" && formData.onset_type === "no_labour") {
            list.push({ type: "warning", msg: "PROM with no labour — Monitor for infection", icon: "💧" });
        }
        if (riskFactors.length >= 3) {
            list.push({ type: "warning", msg: "Multiple risk factors identified (" + riskFactors.length + ") — High-risk delivery", icon: "⚡" });
        }
        return list;
    }, [formData, riskFactors]);

    /* ═══ Computed: Risk level ═══ */
    const riskLevel = useMemo(function() {
        let hasCritical = false;
        let hasHigh = false;
        riskFactors.forEach(function(key) {
            const factor = RISK_FACTORS.find(function(f) { return f.key === key; });
            if (factor) {
                if (factor.severity === "critical") hasCritical = true;
                if (factor.severity === "high") hasHigh = true;
            }
        });
        if (hasCritical || alerts.some(function(a) { return a.type === "critical"; })) return "critical";
        if (hasHigh || riskFactors.length >= 3) return "high";
        if (riskFactors.length > 0) return "medium";
        return "low";
    }, [riskFactors, alerts]);

    /* ═══ Board stats ═══ */
    const boardStats = useMemo(function() {
        const stats = { admitted: 0, in_labour: 0, delivered: 0, total: admissions.length };
        admissions.forEach(function(a) {
            if (a.status === "admitted") stats.admitted++;
            if (a.status === "in_labour") stats.in_labour++;
            if (a.status === "delivered") stats.delivered++;
        });
        return stats;
    }, [admissions]);

    /* ═══ ANC summary for quick display ═══ */
    const ancSummary = useMemo(function() {
        if (!bookingData) return null;
        return {
            ancNumber: bookingData.anc_number,
            gravida: bookingData.gravida,
            parity: bookingData.parity,
            edd: bookingData.edd,
            lnmp: bookingData.lnmp,
            bloodGroup: bookingData.blood_group,
            rhesus: bookingData.rhesus_factor,
            status: bookingData.status,
        };
    }, [bookingData]);

    return {
        bookingData: bookingData,
        loadingBooking: loadingBooking,
        admissionRecord: admissionRecord,
        loadingAdmission: loadingAdmission,
        formData: formData,
        handleChange: handleChange,
        riskFactors: riskFactors,
        toggleRiskFactor: toggleRiskFactor,
        saveAdmission: saveAdmission,
        saving: saving,
        alerts: alerts,
        riskLevel: riskLevel,
        gestationalAge: gestationalAge,
        ancSummary: ancSummary,
        activeSection: activeSection,
        setActiveSection: setActiveSection,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        admissions: admissions,
        loadingBoard: loadingBoard,
        boardStats: boardStats,
        updateStatus: updateStatus,
        fetchBoard: fetchBoard,
        /* Constants */
        REFERRAL_SOURCES: REFERRAL_SOURCES,
        ONSET_TYPES: ONSET_TYPES,
        MEMBRANE_STATUS: MEMBRANE_STATUS,
        FETAL_PRESENTATIONS: FETAL_PRESENTATIONS,
        FETAL_POSITIONS: FETAL_POSITIONS,
        FETAL_LIES: FETAL_LIES,
        ADMISSION_STATUSES: ADMISSION_STATUSES,
        RISK_FACTORS: RISK_FACTORS,
    };
};
